import * as React from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useLazyWorkplacePatientsQuery } from '../../../../graphql/graphql';
import { useAppContext } from '../../../../Services/AppContext';
import { FieldError } from 'react-hook-form';
import { useEffect, useState } from 'react';
import useWorkplaces from '../../../Workplace/Hooks/useWorkplaces';
import ReactSelect from 'react-select';
import { useEffectAsync } from '../../../../helpers';
import PatientSelectItem from '../PatientSelect/PatientSelectItem';
import useTranslation from '../../../../Hooks/useTranslation';

interface PatientSelectProps {
	onChange?: (value: SelectValue) => void;
	workplaceId?: string;
	value?: SelectValue;
	error?: FieldError;
	placeholder?: string;
	searchInAllWorkplaces?: boolean;
	useBirthNumber?: boolean;
	useOriginalId?: boolean;
}

export interface PatientSelectValue extends SelectValue {
	item: any;
}

const PatientAsyncSelect: React.FC<PatientSelectProps> = props => {
	const {
		workplaceId,
		value,
		onChange,
		searchInAllWorkplaces = false,
		useBirthNumber = false,
		useOriginalId = false,
	} = props;
	const [searchPatients] = useLazyWorkplacePatientsQuery();
	const { userData } = useAppContext();
	const { workplaceIds } = useWorkplaces();
	const { t } = useTranslation();
	const [innerValue, setInnerValue] = useState<any>();
	const [selectInputValue, setSelectInputValue] = useState<string>('');
	const [options, setOptions] = useState<SelectValue[]>([]);
	const [page, setPage] = useState(1);
	const components = {
		DropdownIndicator: () => null,
		IndicatorSeparator: () => null,
	};
	// @ts-ignore
	components['Option'] = PatientSelectItem;

	const handleChange = (val: any) => {
		onChange?.(val);
	};

	const currentWorkplaceId: string = workplaceId || userData.workplaceId;

	const handleSearchItem = async (
		searchInput: string,
		page?: number,
	): Promise<SelectValue[]> => {
		const { workplacePatients } = await searchPatients({
			fulltext: searchInput,
			workplaceIds: searchInAllWorkplaces ? workplaceIds : [currentWorkplaceId],
			pagination: {
				page: page || 1,
			},
		}).unwrap();

		const items: PatientSelectValue[] = [];

		for (const workplacePatient of workplacePatients?.data || []) {
			if (workplacePatient?.user) {
				let newUserId = workplacePatient.user.id;
				if (useOriginalId && workplacePatient.user.originalId) {
					newUserId = workplacePatient.user.originalId;
				}
				if (useBirthNumber && workplacePatient.user?.profile?.birthNumber) {
					newUserId = workplacePatient.user.profile.birthNumber;
				}
				if (!items.some(item => item.value === newUserId)) {
					items.push({
						value: newUserId,
						label: workplacePatient.user.profile?.fullName || '',
						item: workplacePatient,
					});
				}
			}
		}

		return items;
	};

	const handleLoadOptionsBySearch = async (inputValue: string) => {
		setPage(1);
		const newOptions = await handleSearchItem(inputValue, 1);
		setOptions(newOptions);
	};

	const handleLoadMoreOptions = async () => {
		setPage(prev => prev + 1);
		const newOptions = await handleSearchItem(selectInputValue, page + 1);
		setOptions(prevOptions => [...prevOptions, ...newOptions]);
	};

	useEffectAsync(async () => {
		if (!selectInputValue || selectInputValue.length < 2) {
			setOptions([]);
			return;
		}
		await handleLoadOptionsBySearch(selectInputValue);
	}, [selectInputValue]);

	useEffect(() => {
		setInnerValue(value);
	}, [value]);

	return (
		<ReactSelect
			classNamePrefix={'reactSearchSelect'}
			options={options}
			value={innerValue}
			onChange={handleChange}
			//@ts-ignore
			menuPortalTarget={document.body}
			isSearchable={true}
			onMenuScrollToBottom={handleLoadMoreOptions}
			onInputChange={setSelectInputValue}
			components={components}
			placeholder={t('base.shared.pick')}
		/>
	);
};

export default PatientAsyncSelect;
